import { CompanyWorker } from "model/Company";
import { CompanyGigShift } from "model/Gig";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { mapStaffProfileResponseV3ToData } from "services/api/company/staff.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import type { Worker as ModalWorker } from "../../../organizationalStructure/chooseWorkersModal.types";
import { ChooseWorkersModal } from "../../../organizationalStructure/chooseWorkersModal.component";
import { WorkerDropdown } from "../../../salary/workerDropDown.component";
import {
  ChooseWorkerSearchWrap,
  ChooseWorkersContainer,
  ScheduleMultipleButtonWrap,
  WorkerListWrap,
} from "../createGigModal.styled";
import {
  getDefaultPeriodRanges,
  mapStaffProfileToModalWorker,
  mergeCopiedShifts,
  type PeriodRanges,
} from "./scheduleGigForm.logic";
import { RepeatScheduleModal } from "./repeatScheduleModal.component";
import { WorkerList } from "./workerList.component";

type Props = {
  setSelectedWorkers: React.Dispatch<React.SetStateAction<ModalWorker[]>>;
  selectedWorkers: ModalWorker[];
  setShifts: React.Dispatch<React.SetStateAction<CompanyGigShift[]>>;
};

export const ScheduleGigForm: React.FC<Props> = (props) => {
  const WORKER_DROPDOWN_WIDTH = 520;
  const { t } = useTranslation();
  const { selectedWorkers, setSelectedWorkers, setShifts } = props;
  const [, , alertDispatch] = useAlertStore();
  const [companyState] = useCompanyStore();
  const [selectedWorker, setSelectedWorker] = useState<
    CompanyWorker | undefined
  >(undefined);
  const [showChooseSeveral, setShowChooseSeveral] = useState(false);
  const [repeatScheduleWorker, setRepeatScheduleWorker] =
    useState<ModalWorker | null>(null);
  const [repeatSchedulePeriods, setRepeatSchedulePeriods] =
    useState<PeriodRanges>(getDefaultPeriodRanges);
  const { copyFromDates, createInDates } = repeatSchedulePeriods;

  const selectedWorkerKeys = useCallback(
    (worker: ModalWorker): string => String(worker.workerId ?? worker.id),
    [],
  );

  const selectedWorkerIdsKey = selectedWorkers
    .map(selectedWorkerKeys)
    .sort()
    .join("|");

  useEffect(() => {
    const companyId = companyState.company?.id;
    const workerId = selectedWorker?.id;

    if (!companyId || !workerId) return;

    const alreadySelected = selectedWorkers.some(
      (worker) => selectedWorkerKeys(worker) === String(workerId),
    );
    if (alreadySelected) return;

    let cancelled = false;
    Api()
      .company.staff.getWorkerProfile(companyId, Number(workerId))
      .then((res) => {
        if (cancelled) return;
        const workerProfile = mapStaffProfileToModalWorker({
          workerId: Number(workerId),
          workerProfile: mapStaffProfileResponseV3ToData(res.data),
        });

        let didAdd = false;
        setSelectedWorkers((prev) => {
          if (
            prev.some(
              (worker) => selectedWorkerKeys(worker) === String(workerId),
            )
          ) {
            return prev;
          }
          didAdd = true;
          return [...prev, workerProfile];
        });

        if (didAdd) {
          setSelectedWorker(undefined);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "alert",
            title: t("Alerts.OhNo"),
            message: t("Alerts.SomethingWentWrong"),
          },
        });
        console.error("Failed to load worker profile", {
          companyId,
          workerId,
          error,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedWorkerKeys,
    selectedWorker?.id,
    companyState.company?.id,
    selectedWorkerIdsKey,
    selectedWorkers,
    alertDispatch,
    setSelectedWorkers,
    t,
  ]);

  const addWorkersToList = useCallback(
    (selected: ModalWorker[]) => {
      setShowChooseSeveral(false);

      setSelectedWorkers((prev) => {
        const existing = new Set(prev.map(selectedWorkerKeys));

        const incoming = selected
          .map((worker) => ({
            ...worker,
            workerId: worker.workerId ?? worker.id,
          }))
          .filter(
            (worker) => !existing.has(String(worker.workerId ?? worker.id)),
          );

        return incoming.length ? [...prev, ...incoming] : prev;
      });
    },
    [selectedWorkerKeys, setSelectedWorkers],
  );

  const handleCopyScheduleSuccess = useCallback(
    (copiedShifts: CompanyGigShift[]): { addedShiftCount: number } => {
      let addedShiftCount = 0;

      setShifts((previousShifts) => {
        const result = mergeCopiedShifts({
          existingShifts: previousShifts,
          copiedShifts,
        });

        addedShiftCount = result.addedShiftCount;
        return addedShiftCount === 0
          ? previousShifts
          : [...previousShifts, ...result.acceptedShifts];
      });

      return { addedShiftCount };
    },
    [setShifts],
  );

  const handleRepeatScheduleClick = useCallback((worker: ModalWorker): void => {
    setRepeatSchedulePeriods(getDefaultPeriodRanges());
    setRepeatScheduleWorker(worker);
  }, []);

  return (
    <>
      <ChooseWorkersContainer>
        <ChooseWorkerSearchWrap>
          <WorkerDropdown
            selectedWorker={selectedWorker}
            setSelectedWorker={setSelectedWorker}
            onlyActiveWorkers
            width={WORKER_DROPDOWN_WIDTH}
            lazyLoad
          />
        </ChooseWorkerSearchWrap>
        <ScheduleMultipleButtonWrap>
          <SecondaryButton
            size="small"
            onClick={() => setShowChooseSeveral(true)}
            label={t("CreateShift.ChooseSeveral")}
          />
        </ScheduleMultipleButtonWrap>
      </ChooseWorkersContainer>

      <WorkerListWrap>
        <WorkerList
          selectedWorkers={selectedWorkers}
          setSelectedWorkers={setSelectedWorkers}
          onRepeatScheduleClick={handleRepeatScheduleClick}
        />
      </WorkerListWrap>

      {showChooseSeveral && (
        <ChooseWorkersModal
          setShowModal={setShowChooseSeveral}
          showModal={showChooseSeveral}
          onClick={addWorkersToList}
          title={t("CreateShift.ChooseSeveral")}
          info={t("CreateShift.ChooseSeveralInfo")}
          buttonTitle={t("CreateShift.Choose")}
          selected={selectedWorkers}
          filter={"InWorkGroup"}
          showPaymentMethod
        />
      )}

      <RepeatScheduleModal
        isOpen={repeatScheduleWorker !== null}
        companyId={companyState.company?.id ?? null}
        sourceWorker={repeatScheduleWorker}
        initialCopyFromDates={copyFromDates}
        initialCreateInDates={createInDates}
        onCopyScheduleSuccess={handleCopyScheduleSuccess}
        onClose={() => setRepeatScheduleWorker(null)}
      />
    </>
  );
};
